import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  FormGroup,
  Col,
  Row,
  Container,
  Table,
} from "reactstrap";
import "./index.css";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import api from "api";
import { UserInfo } from "model/user";
import { ApiResponse} from "model/response";
import _ from "lodash";
import ReactPaginate from "react-paginate";
import {
  Table as TableType,
  TableDetailResponse,
} from "model/table";
import { ColumnListingResponse, Column } from "model/column";
import { useRouteMatch } from "react-router-dom";
import EditableInput from "components/EditableInput";
import TransformationGraph from "pages/Transformations/components/TransformationGraph";

const PAGE_LIMIT = 50;
interface ListingData {
  columns: Array<Column>;
  page: number;
  total: number;
}

export default function TableDetail() {
  const match = useRouteMatch("/table/:table_id");
  const params: any = match?.params;
  const tableId = params?.table_id;

  const companyId: any = api.getCurrentCompanyId();
  const [table, setTable] = useState<TableType | null>(null);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [listingData, setListingData] = useState<ListingData>({
    columns: [],
    page: 0,
    total: 0,
  });

  const cantEditColumn = currentUser?.is_admin || currentUser?.is_company_admin || currentUser?.is_data_practitioner;
  const canViewChangelog = currentUser?.is_admin || currentUser?.is_company_admin;

  useEffect(() => {
    getTableDetail();
  }, []);

  useEffect(() => {
    fetchData();
  }, [listingData?.page]);

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
  };

  useEffect(() => {
    initData();
  }, []);

  const fetchData = async () => {
    let url = `column?company_id=${companyId}&table_id=${tableId}&limit=${PAGE_LIMIT}&page=${listingData?.page}`;
    if (searchKeyword !== "") {
      url = url + `&keyword=${searchKeyword}`;
    }
    const res: ApiResponse = await ApiCaller.get(url);
    if (res?.status === "ok") {
      const data: ColumnListingResponse = res?.data;
      setListingData({
        ...listingData,
        ...{ columns: data?.columns, total: data?.total },
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const getTableDetail = async () => {
    const url = `table/${tableId}`;
    const res: ApiResponse = await ApiCaller.get(url);
    if (res?.status === "ok") {
      const data: TableDetailResponse = res?.data;
      setTable(data.table);
    }
  };

  if (loading) return <Loading />;

  return (
    <Container className="column__detail">
      <h3>{table?.name}</h3>
      <Table responsive className="table__info">
        <thead>
          <tr><th>Table info</th></tr>
        </thead>
        <tbody>
          <tr key='table_project'>
            <td className="table__info_attribute_label">Project ID</td>
            <td>{table?.project_id}</td>
          </tr>
          <tr key='table_dataset'>
            <td className="table__info_attribute_label">Dataset ID</td>
            <td>{table?.dataset_id}</td>
          </tr>
          <tr key='table_creation_time'>
            <td className="table__info_attribute_label">Created at</td>
            <td>{table?.creation_time}</td>
          </tr>
          <tr key='table_last_modified'>
            <td className="table__info_attribute_label">Last modified</td>
            <td>{table?.last_modified}</td>
          </tr>
          <tr key='table_type'>
            <td className="table__info_attribute_label">Table type</td>
            <td>{table?.table_type}</td>
          </tr>
          <tr key='table_num_rows'>
            <td className="table__info_attribute_label">Total records</td>
            <td>{table?.num_rows.toLocaleString()}</td>
          </tr>
          <tr key='table_description'>
            <td className="table__info_attribute_label">Description</td>
            <td className="custom-textarea-multi-line">{table?.description}</td>
          </tr>
          <tr key='table_label'>
            <td className="table__info_attribute_label">Labels</td>
            <td>
              {table?.labels &&
                Object.keys(table?.labels).map(key => (
                  <span key={key} className="item__label">{key}:<b>{table?.labels[key]}</b><br/></span>
                ))
              }
            </td>
          </tr>
        </tbody>
      </Table>
      {table?.query_graphs &&
        table?.query_graphs.nodes &&
        table?.query_graphs.nodes.length > 0 && (
          <Row>
            <Col xs={12}>
              <TransformationGraph graph={table?.query_graphs} />
              <br />
            </Col>
          </Row>
        )}
      <Row>
        <Col xs={12}>
          <FormGroup>
            <Input
              onKeyPress={(e: any) => {
                if (e.code == "Enter") {
                  fetchData();
                }
              }}
              placeholder="Search by name, code name, description... of column"
              onChange={(e) => {
                setSearchKeyword(e.target.value);
                setListingData({
                  ...listingData,
                  ...{ page: 0 },
                });
              }}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <Button
            color="primary"
            onClick={() => {
              fetchData();
            }}
          >
            Search
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ReactPaginate
            previousLabel={"<"}
            nextLabel={">"}
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={Math.floor(listingData?.total / PAGE_LIMIT) + 1}
            marginPagesDisplayed={3}
            pageRangeDisplayed={2}
            initialPage={0}
            onPageChange={async (page) => {
              setListingData({
                ...listingData,
                ...{ page: page.selected },
              });
            }}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <br />
          <Table responsive striped>
            <thead>
              <tr>
                <th>Column Name</th>
                <th>Code Name</th>
                <th>Description</th>
                <th>Calculation Note</th>
                <th>Last Edit By</th>
                {canViewChangelog && <th></th>}
              </tr>
            </thead>
            <tbody>
              {listingData.columns.map((column) => (
                <tr key={`${column.id}`}>
                  <td>{column.name}</td>
                  <td>{column.code_name}</td>
                  <td className='custom-textarea-multi-line'>
                    {cantEditColumn ?
                      <EditableInput
                        index={"column"}
                        id={column.id}
                        updateKey={"description"}
                        updateValue={column?.description}
                      /> : <span>{column.description}</span>
                    }
                  </td>
                  <td className='custom-textarea-multi-line'>
                    {cantEditColumn ?
                      <EditableInput
                        index={"column"}
                        id={column.id}
                        updateKey={"calculation_note"}
                        updateValue={column?.calculation_note}
                      /> : <span>{column.calculation_note}</span>
                    }
                  </td>
                  <td>{column.last_edit_by_email}</td>
                  {canViewChangelog &&
                    <td>
                      <a href={`/dictionary/changelog/column/${column.id}`}>
                        changelog
                      </a>
                    </td>
                  }
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}
